// GROUND-M M2 CLIENT: the quadtree ground (APP_GROUNDM.md), the ONLY ground:
// meta.groundm (the bake stage groundm/stage.mjs writes it). TerrainManager
// owns it and hands it update / groundDrawn / skinY / nearReady; bores and
// pave are cut into the nodes at bake (groundm/bakectx.mjs).
//
// Per frame: morph.js drawSet picks the drawn nodes (screen error, R_STAND,
// geomorph m, hysteresis; a wanted child not resident is a MISS — counted,
// the parent's quadrant holds), slotsOf their nine morph values (uniforms,
// no geometry), and on a change of the drawn set stitch.js re-stitches the
// nodes whose signature changed (a cached Work otherwise) — no skirts,
// watertight by construction — in the decode worker (the whole set off this
// thread, t37, with each geometry's bounds; the geometries are built here
// stitchMs a frame), else here across frames, stitchMs a frame (STITCH_PH), coarse
// mesh first: the new set shows when every node of it is stitched, the last
// set draws until then (never a node unstitched; its morph values: this
// frame's where the selection still holds the entry). A still view (P-a): the
// selection is morph.js stillDraw's (a settled frame at the exact same view
// and residency generation _gen() returns again: no walk, no want / filter /
// sig rebuilt), the slots morph.js slotPlan's per sig (slotsRun a frame, not
// run at all while the set shown and its nodes stay), and the per-frame node
// scans run only on a change (the decode set, a fail, want / drawn / job /
// residency for the eviction). Residency: what the eye draws, plus
// what it will draw T_LEAD s ahead along its velocity (prefetch), plus every
// such node's parent (its delta reads the parent's mesh in the decoder);
// loads nearest-need first (a stand miss, then by level, coarse first), at
// most loads_in_flight open; the rest an LRU of cache nodes.
//
// Colour (M2b): the virtual texture pool (gmtex.js GmTex, `vt`) — a node
// loads no texture, its material has no map, the V_MORPH program reads the
// pool (terrain.js wantVt); a node draws once the pool holds a level at or
// coarser than its own under it (n.texOk, latched; until then it is a miss
// and its parent holds).
//
// THE FAR FIELD (rearch-r5d-far-tree-client; meta.groundm.outer, APP_GROUNDM
// "The far field"): the same tree runs out to the outer hull (top 9, tx / ty
// may be negative; a child missing outside the frame: morph.js prepIndex's
// hold rule). A node's class (_cls): FRAME-BUILT 'f' (k <= outer.first and
// meets the frame: the terrain's nodes, V_EXT where it straddles), a
// STRADDLER 'c' (an outer node meeting the frame: the pool inside, the far
// atlas beyond — terrain.js V_FAR) or BEYOND 'o' (wholly outside: the atlas
// only, V_FAR | V_FARONLY). Colour: 'f' / 'c' the pool's texReady (latched; a
// straddler never waits on the atlas — the near chain runs through it, and an
// outer fragment of a sector not yet resident is the pool's no-slot grey),
// 'o' the atlas holding every sector meeting it (faratlas.js covers; per
// atlas version, never latched). 'o' nodes fetch as bulk (needscore.js
// groundm rows, keep), the rest near. THE APRONS (index.json `aprons`, the
// frame chunks straddling a non-chunk-aligned frame edge): an apron draws
// exactly while a drawn frame-built entry overlaps its L4 square (morph.js
// apronsDrawn), with the far material clipped to its clip rect (V_CLIP), no
// stitch (its tuck hides the seam), no geomorph.
//
// A drawn vertex: position (world x, aH, world z; f32 — a shared vertex is
// one world mm on both sides, so the two copies are bit-identical) + the i8
// delta code (stitch.js DQ). The vertex shader (terrain.js V_MORPH) derives
// the morph tag and the texture uv from the position and the node's origin.
import * as THREE from 'three';
import { prepIndex, newState, drawSet, LOD_M, newStill, stillDraw, selView, sameView, slotPlan, slotsRun } from '../../groundm/morph.js';
import { stitchPlan, stitchStep, stitchDone } from '../../groundm/stitch.js';
import { nodeSide, indexNode, groundAt, offFrame } from '../../groundm/lookup.js';
import { apronsDrawn } from '../../groundm/morph.js';
import { decodeNode, meshOf } from '../../groundm/codec.js';
import { groundCells, gridOf } from './cellruns.js';
import { fetchAsset } from './assetq.js';
import { decodeBase, geoArrays, drawMsg } from './gmdecode.js';
import { curveOf, curveDrop, curveRange } from './curve.js';
import RENDER_CFG from './renderconfig.json';
import { sec, phase, budget, xrOn } from './sched.js';
import { groundOrder } from './visframe.js';
import { classDraw } from './device.js';
import { drawK } from './adaptres.js';

const CFG = RENDER_CFG.groundm || {};
const SEL_PX = CFG.px ?? 4;                 // split at this screen error (the gate's --sel)
// ... × the class's draw rule / the draw rung in force (device.js CLASS_DRAW,
// adaptres.js drawK; quest-budget): a coarser split of the same error-bounded
// tree — fewer draws, every node still within its error. In the still cache's
// key (morph.js SEL_KEYS px), so a move re-selects at once
const selPx = () => SEL_PX * classDraw('px') / drawK();
// R_STAND of the class (device.js CLASS_DRAW stand; quest-ground): morph.js
// LOD_M stand / standW × the class rule — the selection's (in the still key),
// the stand-miss priority and gmtex.js vtLatch read this one
export const standOf = (cls) => {
  const f = classDraw('stand', cls);
  return { stand: LOD_M.stand * f, standW: LOD_M.standW * f };
};
const T_LEAD = CFG.t_lead ?? 3;             // prefetch horizon (s) along the velocity
const CACHE = CFG.cache ?? 192;             // resident nodes beyond the wanted ones (LRU)
const STITCH_MS = CFG.stitch_ms ?? 4;       // stitch work a frame in play (at least one node)
// ... behind the Map and through the Beam: more a frame (nothing drawn to
// keep smooth yet; the latch waits on the set shown, t37); an XR frame: its cap
const STITCH_PH = { map: 12, beam: 12, play: STITCH_MS };
const stitchMs = () => Math.min(STITCH_PH[phase()] ?? STITCH_MS, xrOn() ? budget() : Infinity);
const K_QUEST = 792.4;                      // px / rad without a pixel angle (the gate's)

/* global __GM_WORKER_SRC__ */
// the decode worker (gmworker.js inlined by scripts/build-client.js); null:
// decode on the main thread (no bundle / Worker, a failure)
function gmPool() {
  if (typeof __GM_WORKER_SRC__ === 'undefined' || typeof Worker === 'undefined' || typeof Blob === 'undefined') return null;
  try {
    const w = new Worker(URL.createObjectURL(new Blob([__GM_WORKER_SRC__], { type: 'text/javascript' })));
    const pool = { w, id: 0, wait: new Map(), dead: false };
    w.onmessage = (e) => { const p = pool.wait.get(e.data.id); if (p) { pool.wait.delete(e.data.id); p(e.data); } };
    w.onerror = () => { pool.dead = true; for (const p of pool.wait.values()) p({ error: 'worker gone' }); pool.wait.clear(); };
    return pool;
  } catch { return null; }
}

// a node's number (k; |tx|, |ty| < 2^19: k·2^40 + ty·2^20 + tx is one
// number per node — the far tree's W / N nodes are negative)
const numKey = (k, tx, ty) => (k * 1048576 + ty) * 1048576 + tx;
const _hit = { t: -1 };             // groundDrawn's triangle hit (reset per call)

export class GroundTree {
  // tm: the TerrainManager (materials, group, near latch); cfg:
  // meta.groundm = { index: 'groundm/index.json', rev, top }; vt: the texture
  // pool (gmtex.js GmTex)
  constructor(tm, cfg, vt) {
    this.tm = tm;
    this.cfg = cfg;
    this.vt = vt;
    this.focus = null;           // focus.js (terrain.js updateFocus): the pool's optic tiles
    // the coarsest drawn level: the bake's (meta.groundm.top; groundm/stage.mjs)
    this.top = cfg.top ?? CFG.top ?? 4;
    // the far field (header): meta.groundm.outer + its atlas (terrain.js), or none
    this.outer = cfg.outer || null;
    this.atlas = this.outer ? tm.farAtlas : null;
    if (this.outer && !this.atlas) throw new Error('groundm: a far tree needs the far atlas (renderconfig vt.far)');
    this.world = { w: tm.extentX, h: tm.extentZ };
    this.aprons = new Map();     // 'tx_ty' -> { row, st: 'idle' | 'fetch' | 'ready' | 'fail', mesh }
    // the earth curvature every emitted vertex takes (curve.js; null: no far field)
    this.curve = curveOf(tm.meta);
    this.index = null;
    this.nodes = new Map();      // key -> { k, tx, ty, row, st: 'fetch' | 'decode' | 'ready', bytes, base, mesh, wsig (the stitch sig shown), used }
    this.st = newState();
    this.drawn = new Map();      // key -> drawn node (this frame)
    this._drawnN = new Map();    // ... by number (numKey: groundDrawn's lookup, no string a call)
    this._drawSig = '';
    this._sCache = new Map();    // stitch.js cache, a job stitched here (no worker): key -> { work, sig }
    this._shown = new Map();     // the drawn set shown (its entries; _drawSig its sig)
    this._job = null;            // the set being stitched: { sig, draw, keys, geo, ms, near, P (here) | id, res (the worker) }
    this._queue = [];
    this._inflight = 0;
    this._inN = { near: 0, bulk: 0 };   // ... per fetch class (_drain: each class its own loads_in_flight)
    this._maxLoads = (RENDER_CFG.focus && RENDER_CFG.focus.loads_in_flight) ?? 6;
    this._pool = gmPool();
    this._cache = new Map();     // main-thread decode: key -> decoded mesh
    this._vel = { x: 0, z: 0 };
    this._last = null;
    this._aheadAt = 0;
    this._ahead = [];            // the index nodes drawn T_LEAD s ahead
    this._aheadV = null;         // ... their view + gen (morph.js selView: a pure function of both)
    this.leadEye = null;         // {x,y,z} set by the caller (the beam flight): the ahead eye, else T_LEAD on the velocity
    // THE STILL CACHE (header): residency generation = _rgen (a node decoded /
    // evicted) + the pool's vt.resGen; the last frame's want / draw / sig / slots
    this._rgen = 0;
    this._still = newStill();
    this._rdy = (key) => this._canDraw(key);
    this._want = null; this._wantR = null; this._wantA = null;
    this._dR = null; this._dGen = -1; this._dDraw = null; this._dSig = '';
    this._plans = new Map();     // sig -> morph.js slotPlan (the last few)
    this._mShown = null; this._mDrawn = null;
    this._qWant = null; this._fWant = null; this._failGen = 0; this._fGen = -1;
    this._dec = new Set();       // nodes in st 'decode' (_decodePending runs while any)
    this._evK = null;
    this.stats = { frames: 0, miss: 0, standMiss: 0, standPop: 0, missNow: 0, standMissNow: 0, drawn: 0, tris: 0, vbytes: 0,
      decoded: 0, decodeMs: 0, stitches: 0, stitchMs: 0, restitched: 0,
      // the near wait since the last jump (t37; PROTOCOL.md load groundm):
      // its stamps, and its ms by the first stage that blocked it (_waitOf)
      t_jump: 0, t_gnear: 0, w_none: 0, w_net: 0, w_dec: 0, w_tex: 0, w_vt: 0, w_job: 0 };
    this._gWait = null;          // the near wait: { at } (the last frame) from a jump until near
    this._shownNear = false;     // the set shown met the near rule when it was taken
    // indexRaw: the parsed index, shared read-only (ring.js buildRingTex's relief rows: no second fetch)
    this.indexRaw = fetchAsset(`${tm.baseUrl}/${cfg.index || 'groundm/index.json'}`, { prio: 'near', as: 'json' });
    this._ready = this.indexRaw
      .then((ix) => {
        if (!ix || !ix.nodes) throw new Error('groundm: no index');
        const bb = tm.meta.bbox, nodes = new Map();
        // under: the node's measured undershoot of L0 (the cull's ground bound, viscull.js);
        // y0 / y1 the drawn range: the flat one less the curvature's drop over the node
        for (const n of ix.nodes) {
          const s = nodeSide(n.k), [dn, df] = curveRange(this.curve, n.tx * s, n.ty * s, (n.tx + 1) * s, (n.ty + 1) * s);
          nodes.set(`${n.k}_${n.tx}_${n.ty}`, { k: n.k, tx: n.tx, ty: n.ty, err: n.tol, y0: n.y0 - df, y1: n.y1 - dn, br: n.br, under: n.under });
        }
        this.index = prepIndex({ nodes, world: { w: bb.e1 - bb.e0, h: bb.n1 - bb.n0, outer: !!this.outer } });
        if (this.outer) for (const a of ix.aprons || []) this.aprons.set(`${a.tx}_${a.ty}`, { row: a, st: 'idle', mesh: null });
      });
  }

  get pending() { return this._queue.length + this._inflight; }
  // the near-class share (frame nodes, `_fetchCls` 'near'): the VR landing's settle counts these — a far node
  // ('o', bulk) waits on the asset queue's Beam pause, so holding the landing can never drain it (w59, box 2026-11-03)
  get pendingNear() {
    let q = 0;
    for (const key of this._queue) { const n = this.nodes.get(key); if (n && n.st === 'queued' && this._fetchCls(n) === 'near') q++; }
    return q + this._inN.near;
  }

  // the selection's options this frame. K: the naked eye's (the texture
  // pool's fill too); while the focus wedge is armed (the optic: desktop /
  // phone band or view arm, the raised VR optic, its linger) the main.js
  // hooks give the eye's 1x pixel angle (basePixelAngle) and the optic's
  // true one (opticPixelAngle): the optic's K inside the wedge only
  // (morph.js wedge, t40). Without them: the view's one K everywhere.
  _opt() {
    const tm = this.tm, f = this.focus;
    const pa = typeof tm.pixelAngle === 'function' ? tm.pixelAngle() : tm.pixelAngle;
    const opa = f && f.active && typeof tm.opticPixelAngle === 'function' ? tm.opticPixelAngle() : null;
    const bpa = opa > 0 && typeof tm.basePixelAngle === 'function' ? tm.basePixelAngle() : null;
    const wedge = bpa > 0 ? { ox: f.ox, oz: f.oz, ax: f.ax, az: f.az, half: f.halfRad, K: 1 / opa } : null;
    const K = wedge ? 1 / bpa : pa > 0 ? 1 / pa : K_QUEST;
    return { K, wedge, px: selPx(), ...standOf(), quads: true, top: this.top,
      qok: (key) => { const n = this.nodes.get(key); return !!(n && n.base && n.base.qok); } };
  }

  // the residency generation (stillDraw's gen): every change _canDraw / qok read
  _gen() { return this._rgen + this.vt.resGen + (this.atlas ? this.atlas.version : 0); }

  // a node's class (header): 'f' frame-built, 'c' a straddler, 'o' beyond
  _cls(k, tx, ty) {
    if (!this.outer) return 'f';
    if (offFrame(tx, ty, nodeSide(k), this.world)) return 'o';
    return k <= this.outer.first ? 'f' : 'c';
  }

  _isReady(key) { const n = this.nodes.get(key); return !!(n && n.st === 'ready'); }

  // decoded and coloured: the pool holds a level at or coarser than the
  // node's under it (latched — the top levels are pinned); a node beyond the
  // frame: the atlas holds every sector meeting it (per atlas version)
  _canDraw(key) {
    const n = this.nodes.get(key);
    if (!n || n.st !== 'ready') return false;
    if (n.texOk) return true;
    if ((n.cls || (n.cls = this._cls(n.k, n.tx, n.ty))) === 'o') {
      const A = this.atlas;
      if (n.texV !== A.version) { const s = nodeSide(n.k); n.texV = A.version; n.texA = A.covers(n.tx * s, n.ty * s, (n.tx + 1) * s, (n.ty + 1) * s); }
      return n.texA;
    }
    return (n.texOk = this.vt.texReady(n.k, n.tx, n.ty));
  }

  update(pos, now) {
    if (!this.index) return;
    sec('gm.vt');   // upd_by / lt_by labels (sched.js sec), flat: each closes the last
    const t = now / 1000;
    // the velocity (smoothed): the prefetch eye runs T_LEAD s ahead
    let jump = !this._last;
    if (this._last) {
      const dt = Math.max(1e-3, t - this._last.t), a = Math.min(1, dt / 0.5);
      this._vel.x += a * ((pos.x - this._last.x) / dt - this._vel.x);
      this._vel.z += a * ((pos.z - this._last.z) / dt - this._vel.z);
      // a jump (placement, GPS): no velocity from it
      if (Math.hypot(pos.x - this._last.x, pos.z - this._last.z) > 200) { this._vel.x = this._vel.z = 0; jump = true; }
    }
    if (jump) {
      const S = this.stats;
      S.t_jump = now; S.t_gnear = 0; S.w_none = S.w_net = S.w_dec = S.w_tex = S.w_vt = S.w_job = 0;
      this._gWait = { at: now };
      this._shownNear = false;   // the set shown is the last place's
    }
    this._last = { x: pos.x, z: pos.z, t };
    const eye = { x: pos.x, y: pos.y, z: pos.z }, opt = this._opt();
    this.vt.update(eye, 1 / opt.K, this.focus && this.focus.levels ? this.focus : null, now);
    sec('gm.select');
    // drawSet's clock is ms (morph.js tMorph); a settled still frame: the last one's
    const gen = this._gen();
    const r = stillDraw(this._still, this.index, eye, opt, this.st, now, this._rdy, gen);
    const S = this.stats;
    S.frames++; S.missNow = r.miss.length; S.standMissNow = r.standMiss;
    S.miss += r.miss.length; S.standMiss += r.standMiss; S.standPop += r.standPop;
    // what to have: the drawn, the misses, T_LEAD ahead (every 250 ms; the
    // same view + gen: the same nodes), and each one's parent chain (the delta's source)
    if (now >= this._aheadAt) {
      sec('gm.ahead');
      this._aheadAt = now + 250;
      const ae = this.leadEye || { x: pos.x + this._vel.x * T_LEAD, y: pos.y, z: pos.z + this._vel.z * T_LEAD };
      if (!this._aheadV || this._aheadV.gen !== gen || !sameView(this._aheadV.v, ae, opt)) {
        const ahead = drawSet(this.index, ae, opt, newState(), now, () => true);
        this._ahead = [...new Set([...ahead.draw.values()].map((e) => e.n))];
        this._aheadV = { v: selView(ae, opt), gen };
      }
    }
    sec('gm.want');
    // key -> priority (lower first): rebuilt when the selection or the ahead set moved
    let want = this._want;
    if (r !== this._wantR || this._ahead !== this._wantA) {
      want = new Map();
      // a node and its parent chain; a chain already at p or less stops the
      // climb (every chain set climbs to the top: its parents hold <= its p)
      const need = (n, p) => {
        for (let c = n; c; c = c.k < this.top ? c.up : null) {
          const q = want.get(c.key);
          if (q !== undefined && q <= p) break;
          want.set(c.key, p);
        }
      };
      const pd = (k, tx, ty) => {
        const s = nodeSide(k), dx = Math.max(tx * s - pos.x, 0, pos.x - tx * s - s), dz = Math.max(ty * s - pos.z, 0, pos.z - ty * s - s);
        return Math.hypot(dx, dz);
      };
      for (const e of r.draw.values()) need(e.n, 2e6 + (this.top - e.n.k) * 1e5 + pd(e.n.k, e.n.tx, e.n.ty));
      // a miss inside its level's R_STAND reach (morph.js Rs) first: the player
      // stands there
      for (const key of r.miss) {
        const n = this.index.nodes.get(key), d = pd(n.k, n.tx, n.ty);
        need(n, (d < opt.stand + opt.standW * (n.k + 1) ? 0 : 1e6) + (this.top - n.k) * 1e5 + d);
      }
      for (const n of this._ahead) need(n, 3e6 + (this.top - n.k) * 1e5 + pd(n.k, n.tx, n.ty));
      this._want = want; this._wantR = r; this._wantA = this._ahead;
    }
    for (const [key, p] of want) {
      let n = this.nodes.get(key);
      if (!n) {
        const { k, tx, ty } = this.index.nodes.get(key);
        n = { k, tx, ty, st: 'queued', prio: p, used: now };
        this.nodes.set(key, n);
        this._queue.push(key);
        this._sortQ = true;
      } else {
        n.used = now;
        // a failed fetch retries after its backoff (a 404 never hammers)
        if (n.st === 'fail' && now >= n.retry) { n.st = 'queued'; this._queue.push(key); n.prio = -1; }
        if (n.st === 'queued' && n.prio !== p) { n.prio = p; this._sortQ = true; }
      }
    }
    // queued and no longer wanted: out of the queue (nothing fetched yet) —
    // a key is queued only from want: nothing to do while want stays
    if (want !== this._qWant) {
      this._qWant = want;
      for (let i = this._queue.length - 1; i >= 0; i--) {
        if (want.has(this._queue[i])) continue;
        const key = this._queue[i];
        this._queue.splice(i, 1);
        if (this.nodes.get(key) && this.nodes.get(key).st === 'queued') this.nodes.delete(key);
      }
    }
    // failed and not wanted: forgotten (on a new want or a new fail)
    if (want !== this._fWant || this._failGen !== this._fGen) {
      this._fWant = want; this._fGen = this._failGen;
      for (const [key, n] of this.nodes) if (n.st === 'fail' && !want.has(key)) this.nodes.delete(key);
    }
    sec('gm.drain');
    this._drain();
    sec('gm.decode');
    this._decodePending();
    sec('gm.draw');
    // drawn: the selection's entries whose node is decoded (a top-level node
    // is selected before it lands: a hole until then, the first load only);
    // the same selection and residency: the same set
    const g2 = this._gen();
    if (r !== this._dR || g2 !== this._dGen) {
      const draw = new Map();
      for (const [pk, e] of r.draw) if (this._canDraw(e.n.key)) draw.set(pk, e);
      this._dR = r; this._dGen = g2; this._dDraw = draw;
      // the drawn set's structure changed: stitch it (cached Works stay); a
      // set still stitching finishes first (then the newest set is taken)
      this._dSig = [...draw.keys()].sort().join(',');
    }
    const draw = this._dDraw, sig = this._dSig;
    // the near rule on this frame's set: nothing missing under the player
    // (R_STAND), the levels the ring rule called near all drawn, and (the
    // pool) their floor colour resident
    const nearMiss = r.miss.filter((key) => this.index.nodes.get(key).k <= this.tm._nearLevels);
    const cond = draw.size > 0 && r.standMiss === 0 && !nearMiss.length && this.vt.nearReady(this.tm._nearLevels);
    if (this._job || sig !== this._drawSig) {
      sec('gm.stitch');
      if (!this._job) this._job = this._take(sig, draw, cond);
      this._stitch();
    }
    sec('gm.morph');
    // the morph values: uniforms only, over the set shown (its sig's slot
    // plan; the same set shown on the same nodes: the values they hold)
    let shown = draw;
    if (sig !== this._drawSig) {
      shown = new Map();
      for (const [pk, e] of this._shown) shown.set(pk, draw.get(pk) || e);
    }
    if (shown !== this._mShown || this.drawn !== this._mDrawn) {
      this._mShown = shown; this._mDrawn = this.drawn;
      const slots = slotsRun(this._plan(this._drawSig, shown), shown);
      for (const [key, n] of this.drawn) {
        const sl = slots.get(key);
        if (sl) n.gm.uMorph.value.set(sl);
        n.m0 = sl ? sl[0] : 1;
      }
    }
    // near: the rule holds now, and the set SHOWN met it — this frame's set
    // shown, or a set that met it when it was taken (the next one may still
    // be stitching: it never holds the latch back); else the latch holds
    const near = cond && ((!this._job && sig === this._drawSig) || this._shownNear || this.tm.nearReady);
    this.tm._setNear(near);
    if (this._gWait) {
      const S = this.stats;
      if (near) { S.t_gnear = now; this._gWait = null; }
      else { S[this._waitOf(draw, r, nearMiss, cond)] += now - this._gWait.at; this._gWait.at = now; }
    }
    sec('gm.evict');
    this._evict(want, now);
  }

  // the slot plan of the set shown (its sig; morph.js slotPlan), the last few kept
  _plan(sig, shown) {
    let P = this._plans.get(sig);
    if (!P) {
      P = slotPlan(shown, this.top);
      this._plans.set(sig, P);
      if (this._plans.size > 4) this._plans.delete(this._plans.keys().next().value);
    }
    return P;
  }

  // a stitch job for a drawn set: in the decode worker (gmdecode.js
  // GmCore.stitch — the whole set at once, off this thread; t37), else here
  // (stitchMs a frame). Either way the set shows atomically when complete
  // (the last set draws until then) and a node's geometry is rebuilt only
  // when its stitch sig moved (n.wsig: a Work is a pure function of its sig)
  _take(sig, draw, near) {
    const J = { sig, draw, keys: new Set([...draw.values()].map((e) => `${e.n.k}_${e.n.tx}_${e.n.ty}`)), geo: new Map(), ms: 0, near, P: null };
    if (this._pool && !this._pool.dead && !this._pool.noStitch) this._post(J, null);
    else J.P = stitchPlan(draw, { get: (key) => this.nodes.get(key).base }, this.top, { cache: this._sCache });
    return J;
  }

  // the job to the worker, with the sigs shown (`have`: no geometry back for
  // them) and the bases it said it lacks (resent); the answer lands on the
  // job — an answer to anything but the current request is dropped
  _post(J, bases) {
    const pool = this._pool, id = ++pool.id, have = {};
    for (const key of J.keys) { const n = this.nodes.get(key); if (n.wsig !== undefined) have[key] = n.wsig; }
    J.id = id; J.res = null;
    pool.wait.set(id, (m) => { if (this._job === J && J.id === id) J.res = m; });
    pool.w.postMessage({ id, stitch: { draw: drawMsg(J.draw), top: this.top, have, bases, curve: this.curve } });
  }

  // the job's frame: the worker's answer, or nodes here (coarse mesh first)
  // until stitchMs, each new Work's geometry built beside it
  _stitch() {
    const J = this._job;
    if (!J.P) {
      const m = J.res;
      if (!m) return;
      if (m.missing) { this._post(J, m.missing.map((key) => [key, this.nodes.get(key).base])); return; }
      if (!m.error) {
        // the geometries (bounds from the worker: no vertex scan here),
        // stitchMs a frame; the set shows when all are built
        J.ms = m.ms;
        const t0 = performance.now(), ms = stitchMs();
        J.gi = J.gi || 0;
        while (J.gi < m.geo.length) {
          const [key, sig, a] = m.geo[J.gi++];
          J.geo.set(key, { sig, geo: this._geoFrom(a, +key.split('_')[0]) });
          if (performance.now() - t0 >= ms) break;
        }
        if (J.gi < m.geo.length) return;
        this._show(J, m.keys.map((key) => [key, J.geo.has(key) ? J.geo.get(key).sig : this.nodes.get(key).wsig]));
        return;
      }
      // the worker cannot stitch (gone, a throw): this thread from now on
      console.warn('groundm stitch', m.error);
      if (this._pool) this._pool.noStitch = true;
      J.P = stitchPlan(J.draw, { get: (key) => this.nodes.get(key).base }, this.top, { cache: this._sCache });
    }
    const P = J.P, t0 = performance.now(), ms = stitchMs();
    while (P.i < P.order.length) {
      const key = P.order[P.i], t1 = performance.now();
      stitchStep(P);
      J.ms += performance.now() - t1;
      const n = this.nodes.get(key), sig = P.sigs.get(key);
      if (n.wsig !== sig) J.geo.set(key, { sig, geo: this._geoFrom(geoArrays(P.works.get(key), n.k, n.tx, n.ty, this.curve), n.k) });
      if (performance.now() - t0 >= ms) break;
    }
    if (P.i < P.order.length) return;
    this._show(J, [...stitchDone(P)].map(([key, r]) => [key, r.sig]));
  }

  // all stitched (res: [[node key, sig]]): the set shows
  _show(J, res) {
    const S = this.stats;
    this._job = null;
    this._drawSig = J.sig;
    this._shown = J.draw;
    this._shownNear = J.near;
    S.stitches++; S.stitchMs += J.ms;
    for (const [key, sig] of res) {
      const n = this.nodes.get(key);
      if (n.wsig !== sig) { this._setGeometry(n, sig, J.geo.get(key).geo); S.restitched++; }
      if (!n.mesh.parent) { this.tm.group.add(n.mesh); n.mesh.updateMatrixWorld(true); }
      if (!this.tm.firstTileAt) this.tm.firstTileAt = performance.now();
    }
    const keys = new Set(res.map(([key]) => key));
    for (const [key, n] of this.drawn) if (!keys.has(key) && n.mesh && n.mesh.parent) this.tm.group.remove(n.mesh);
    this.drawn = new Map([...keys].map((key) => [key, this.nodes.get(key)]));
    this._drawnN = new Map([...this.drawn.values()].map((n) => [numKey(n.k, n.tx, n.ty), n]));
    this._apronSet(J.draw);
    this.tm.gen++;
    let tris = 0, vb = 0;
    for (const n of this.drawn.values()) { tris += n.mesh.geometry.index.count / 3; vb += 13 * n.mesh.geometry.attributes.position.count; }
    S.drawn = this.drawn.size; S.tris = tris; S.vbytes = vb;
  }

  // the fetch queue in priority order, loads_in_flight PER FETCH CLASS (GM-SLOTS): the asset queue
  // holds bulk (the Beam pause, strict priority), so a held far node ('o', bulk) must never take a near
  // node's slot — one shared cap held the near nodes queued from the Beam to the bulk release (probe
  // 2026-10-31, desktop-igpu, Samedan: 6 far L8 / L9 fetches held all 6 slots 7 s → 49 s, the latch by timeout)
  _drain() {
    if (this._sortQ) { this._queue.sort((a, b) => this.nodes.get(a).prio - this.nodes.get(b).prio); this._sortQ = false; }
    const N = this._inN, cap = this._maxLoads;
    for (let i = 0; i < this._queue.length && (N.near < cap || N.bulk < cap);) {
      const key = this._queue[i], n = this.nodes.get(key);
      if (!n || n.st !== 'queued') { this._queue.splice(i, 1); continue; }
      if (N[this._fetchCls(n)] >= cap) { i++; continue; }
      this._queue.splice(i, 1);
      this._load(key, n);
    }
  }

  // beyond the frame: bulk (needscore.js ranks it by its square, keep)
  _fetchCls(n) { return (n.cls || (n.cls = this._cls(n.k, n.tx, n.ty))) === 'o' ? 'bulk' : 'near'; }

  async _load(key, n) {
    n.st = 'fetch';
    this._inflight++;
    const tm = this.tm, prio = this._fetchCls(n);
    this._inN[prio]++;
    try {
      const bin = await fetchAsset(`${tm.baseUrl}/groundm/L${n.k}/${n.tx}_${n.ty}.bin`, { prio, status: true })
        .then((r) => (r.body ? r.body : Promise.reject(new Error('gm ' + r.status))));
      if (this.nodes.get(key) !== n) return;
      n.bytes = bin;
      n.st = 'decode';
      this._dec.add(key);
    } catch {
      if (this.nodes.get(key) === n) { n.st = 'fail'; n.fails = (n.fails | 0) + 1; n.retry = performance.now() + Math.min(30e3, 1e3 << n.fails); this._failGen++; }
    } finally {
      this._inflight--;
      this._inN[prio]--;
      this._drain();
    }
  }

  // fetched nodes whose parent is decoded: decode (worker, else here)
  _decodePending() {
    if (!this._dec.size) return;
    for (const [key, n] of this.nodes) {
      if (n.st !== 'decode' || n.busy) continue;
      const pk = n.k < this.top ? `${n.k + 1}_${n.tx >> 1}_${n.ty >> 1}` : null;
      const parent = pk && this.index.nodes.has(pk) ? pk : null;
      if (parent && !this._isReady(parent)) continue;
      const job = { key, k: n.k, tx: n.tx, ty: n.ty, bytes: n.bytes, parent, parentBytes: parent && n.needParent ? this.nodes.get(parent).raw : null, portal: CFG.portal !== false };
      const done = (m) => {
        n.busy = false;
        if (this.nodes.get(key) !== n) return;
        if (m.error) { console.warn('groundm decode', key, m.error); n.st = 'fail'; n.fails = (n.fails | 0) + 1; n.retry = performance.now() + Math.min(30e3, 1e3 << n.fails); this._dec.delete(key); this._failGen++; return; }
        if (m.wait) { n.needParent = true; return; }     // the decoder lost the parent: its bytes next time
        n.base = m.base; n.raw = n.bytes; n.bytes = null; n.st = 'ready';
        if (m.look) n.idxMesh = this._lookMesh(n, m.look);    // the worker's height index (gmworker.js lookOf)
        this._dec.delete(key); this._rgen++;
        this.stats.decoded++; this.stats.decodeMs += m.ms;
      };
      n.busy = true;
      if (this._pool && !this._pool.dead) {
        const id = ++this._pool.id;
        // decoded: its fetched children go to the worker now, not a frame
        // later each (a level a frame down the parent chain, t37)
        this._pool.wait.set(id, (m) => { done(m); if (n.st === 'ready') this._decodePending(); });
        // copies: the bytes stay here (a re-decode of a child's parent, a
        // worker failure falls back to this thread)
        this._pool.w.postMessage({ id, job: { ...job, bytes: job.bytes.slice(0), parentBytes: job.parentBytes && job.parentBytes.slice(0) } });
      } else {
        try { done(decodeBase(this._cache, job)); } catch (e) { done({ error: String(e) }); }
      }
    }
  }

  // the first stage that holds the near wait this frame (stats w_*): nothing
  // drawable; a blocking miss (a near level's; any, while one is under the
  // player) not fetched / not decoded (its parent chain too) / not coloured
  // — the worst; the pool's near floor tiles; only the stitch job open
  _waitOf(draw, r, nearMiss, cond) {
    if (!draw.size) return 'w_none';
    if (!cond) {
      let w = 0;
      for (const key of r.standMiss ? r.miss : nearMiss) {
        const n = this.nodes.get(key);
        w = Math.max(w, !n || n.st === 'queued' || n.st === 'fetch' || n.st === 'fail' ? 3 : n.st === 'decode' ? 2 : n.texOk ? 0 : 1);
        if (w === 3) break;
      }
      if (w) return ['', 'w_tex', 'w_dec', 'w_net'][w];
      return !this.vt.nearReady(this.tm._nearLevels) ? 'w_vt' : 'w_net';
    }
    return 'w_job';
  }

  // a stitched Work's arrays (gmdecode.js geoArrays: here or the worker's)
  // -> its geometry (built while its set is stitched); the worker's carry
  // their bounds (gmworker.js bounds: bs, y) and their 128 m cell order
  // (cellruns.js groundCells: the P3 cell runs viscull.js draws; k the
  // node's level: its cell side), else both are made here
  _geoFrom(a, k) {
    if (a.cells === undefined) groundCells(a, k);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(a.pos, 3));
    geo.setAttribute('aD', new THREE.BufferAttribute(a.dc, 1));
    geo.setIndex(new THREE.BufferAttribute(a.idx, 1));
    if (a.bs) geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(a.bs[0], a.bs[1], a.bs[2]), a.bs[3]);
    else geo.computeBoundingSphere();
    if (a.y) geo.userData.y = a.y;
    if (a.cells) geo.userData.cells = a.cells;
    return geo;
  }

  // ... the node's mesh, when its set shows (geometry replaced, material kept)
  _setGeometry(n, sig, geo) {
    const P = geo.attributes.position.array, nv = P.length / 3, s = nodeSide(n.k);
    n.wsig = sig;
    if (!n.mesh) {
      n.gm = { uMorph: { value: new Float32Array(9).fill(1) }, uOrigin: { value: new THREE.Vector2(n.tx * s, n.ty * s) }, uSide: { value: s },
        ...this.vt.uniforms, ...(this.atlas ? this.atlas.uniforms : {}) };
      n.ring = { level: n.k, tile_m: s, patch_verts: 257, gm: n.gm };
      let [y0, y1] = geo.userData.y || [Infinity, -Infinity];
      if (!geo.userData.y) for (let v = 0; v < nv; v++) { if (P[3 * v + 1] < y0) y0 = P[3 * v + 1]; if (P[3 * v + 1] > y1) y1 = P[3 * v + 1]; }
      n.y0 = y0; n.y1 = y1;
      n.mask = this.tm._gmMask(n.ring, n.tx, n.ty, y0, y1);
      n.mesh = new THREE.Mesh(geo, this.tm._material({ tex: null, ring: n.ring }, n.mask));
      n.mesh.name = `gm_${n.k}_${n.tx}_${n.ty}`;
      // the ground's draw slot (APP_VISIBILITY §3d, P1): after the building
      // tiles, before every other opaque, so early-z kills what the ground
      // covers; inside it finest level first (groundOrder: near-first early-z)
      n.mesh.renderOrder = groundOrder(n.k);
      n.mesh.frustumCulled = true;
      // static: never moves (world coordinates in the geometry); its world
      // matrix is set once where it joins tm.group (_show)
      n.mesh.matrixAutoUpdate = false;
    } else {
      const old = n.mesh.geometry;
      n.mesh.geometry = geo;
      old.dispose();
    }
  }

  // the drawn levels as ring-likes (terrain.js variantKeepers: one keeper per
  // V_MORPH mask a node of the world can take)
  keeperRings(extentX, extentZ) {
    const out = [];
    for (let k = 0; k <= this.top; k++) {
      const s = nodeSide(k);
      out.push({ level: k, tile_m: s, patch_verts: 257, nxReal: Math.ceil(extentX / s), nyReal: Math.ceil(extentZ / s),
        gm: { uMorph: { value: new Float32Array(9).fill(1) }, uOrigin: { value: new THREE.Vector2() }, uSide: { value: s },
          ...this.vt.uniforms, ...(this.atlas ? this.atlas.uniforms : {}), uClip: { value: new THREE.Vector4() } } });
    }
    return out;
  }

  // THE APRONS (header): the set shown's rule (morph.js apronsDrawn) — an
  // apron joins once fetched and built, leaves with the rule
  _apronSet(draw) {
    if (!this.aprons.size) return;
    const on = apronsDrawn([...this.aprons.values()].map((a) => a.row), draw, this.world, this.outer.first);
    for (const [key, a] of this.aprons) {
      const want = on.has(key);
      a.want = want;
      if (want && a.st === 'idle') this._apronLoad(a);
      if (a.mesh) {
        if (want && !a.mesh.parent) { this.tm.group.add(a.mesh); a.mesh.updateMatrixWorld(true); }
        else if (!want && a.mesh.parent) this.tm.group.remove(a.mesh);
      }
    }
  }

  async _apronLoad(a) {
    const r = a.row, s = nodeSide(r.k);
    a.st = 'fetch';
    try {
      const bin = await fetchAsset(`${this.tm.baseUrl}/groundm/L${r.k}/${r.tx}_${r.ty}.apron.bin`, { prio: 'bulk', status: true })
        .then((x) => (x.body ? x.body : Promise.reject(new Error('gm apron ' + x.status))));
      // GM03 as a node's (codec.js meshOf: node-local m), no delta (aD 0), sunk by the curvature
      const m = meshOf(decodeNode(new Uint8Array(bin))), nv = m.x.length, pos = new Float32Array(3 * nv);
      for (let v = 0; v < nv; v++) {
        const x = r.tx * s + m.x[v], z = r.ty * s + m.z[v];
        pos[3 * v] = x; pos[3 * v + 1] = m.y[v] - curveDrop(this.curve, x, z); pos[3 * v + 2] = z;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setAttribute('aD', new THREE.BufferAttribute(new Int8Array(nv), 1));
      geo.setIndex(new THREE.BufferAttribute(nv < 65536 ? Uint16Array.from(m.idx) : m.idx, 1));
      geo.computeBoundingSphere();
      const gm = { uMorph: { value: new Float32Array(9).fill(1) }, uOrigin: { value: new THREE.Vector2(r.tx * s, r.ty * s) }, uSide: { value: s },
        ...this.atlas.uniforms, uClip: { value: new THREE.Vector4(...r.clip) } };
      const ring = { level: r.k, tile_m: s, patch_verts: 257, gm, apron: true };
      a.mesh = new THREE.Mesh(geo, this.tm._material({ tex: null, ring }, this.tm._gmMask(ring, r.tx, r.ty, r.y0, r.y1)));
      a.mesh.name = `gm_apron_${r.tx}_${r.ty}`;
      a.mesh.renderOrder = groundOrder(r.k);
      a.mesh.matrixAutoUpdate = false;
      a.st = 'ready';
      if (a.want) { this.tm.group.add(a.mesh); a.mesh.updateMatrixWorld(true); }
    } catch (e) {
      console.warn('groundm apron', r.tx, r.ty, e && e.message);
      a.st = 'fail';
    }
  }

  // carve / water registrations: masks again (terrain.js _reclassify)
  reclassify() {
    for (const n of this.nodes.values()) {
      if (!n.mesh) continue;
      const mask = this.tm._gmMask(n.ring, n.tx, n.ty, n.y0, n.y1);
      if (mask === n.mask) continue;
      const old = n.mesh.material;
      n.mesh.material = this.tm._material({ tex: null, ring: n.ring }, mask);
      n.mask = mask;
      old.dispose();
    }
  }

  _evict(want, now) {
    // the free count moves only with want, the drawn / stitched sets or a node ready / gone
    const ek = this._evK;
    if (ek && ek[0] === want && ek[1] === this.drawn && ek[2] === this._job && ek[3] === this._rgen) return;
    this._evK = [want, this.drawn, this._job, this._rgen];
    let spare = 0;
    // (the set being stitched is kept: its Works and geometry are built)
    const free = (key, n) => !want.has(key) && !this.drawn.has(key) && !(this._job && this._job.keys.has(key)) && n.st === 'ready';
    for (const [key, n] of this.nodes) if (free(key, n)) spare++;
    if (spare <= CACHE) return;
    const old = [...this.nodes].filter(([key, n]) => free(key, n))
      .sort((a, b) => a[1].used - b[1].used).slice(0, spare - CACHE);
    for (const [key, n] of old) {
      if (n.mesh) { if (n.mesh.parent) this.tm.group.remove(n.mesh); n.mesh.geometry.dispose(); n.mesh.material.dispose(); }
      this.nodes.delete(key);
      this._sCache.delete(key);
      if (this._pool && !this._pool.dead) this._pool.w.postMessage({ drop: key });
      this._cache.delete(key);
    }
    this.tm.gen++;
    this._rgen++;
    this._evK[3] = this._rgen;
  }

  // the height index the decode worker built for a node (gmworker.js lookOf:
  // _meshOf's indexNode node, its grid packed) — the same node, unpacked
  _lookMesh(n, look) {
    const B = n.base;
    return { x: look.x, z: look.z, y: B.h, idx: B.idx, lab: B.lab, level: n.k, grid: gridOf(look) };
  }

  // the base mesh of a resident node, indexed for height reads (lazy; a
  // node the worker decoded comes with it: _lookMesh)
  _meshOf(n) {
    if (n.idxMesh) return n.idxMesh;
    const B = n.base, x = new Float64Array(B.nv), z = new Float64Array(B.nv);
    for (let v = 0; v < B.nv; v++) { x[v] = B.xm[v] / 1000; z[v] = B.zm[v] / 1000; }
    n.idxMesh = indexNode({ x, z, y: B.h, idx: B.idx, lab: B.lab, level: n.k });
    return n.idxMesh;
  }

  // the drawn ground at (x, z): the finest drawn node there, as its shader
  // draws it inside (y = aH - (1 - m) delta; a stitched border's own values
  // differ from it by the stitch only) — NaN where nothing is drawn. at
  // (optional) gets .k = the level of the node read (yfrom.js: a read off a
  // coarse node is taken again once a finer one is drawn there)
  groundDrawn(x, z, at = null) {
    const hit = _hit;                 // one scratch (a drape pass asks ~24k times)
    hit.t = -1;
    for (let k = 0; k <= this.top; k++) {
      const s = nodeSide(k), tx = Math.floor((x - 0.25) / s), ty = Math.floor((z - 0.25) / s);
      const n = this._drawnN.get(numKey(k, tx, ty));
      if (!n) continue;
      const m = this._meshOf(n), lx = x - tx * s, lz = z - ty * s;
      const y = groundAt(m, lx, lz, hit);
      if (!Number.isFinite(y) || hit.t < 0) return NaN;
      if (at) at.k = k;
      const B = n.base, a = B.idx[3 * hit.t], b = B.idx[3 * hit.t + 1], c = B.idx[3 * hit.t + 2];
      const ax = m.x[a], az = m.z[a], bx = m.x[b] - ax, bz = m.z[b] - az, cx = m.x[c] - ax, cz = m.z[c] - az;
      const den = bx * cz - bz * cx, px = lx - ax, pz = lz - az, l1 = (px * cz - pz * cx) / den, l2 = (bx * pz - bz * px) / den;
      const d = B.d[a] + l1 * (B.d[b] - B.d[a]) + l2 * (B.d[c] - B.d[a]);
      return y - (1 - (n.m0 ?? 1)) * d - curveDrop(this.curve, x, z);
    }
    return NaN;
  }

  // the level of the finest drawn node at (x, z) (groundDrawn's node, no
  // triangle read); -1 where none is drawn
  drawnLevel(x, z) {
    for (let k = 0; k <= this.top; k++) {
      const s = nodeSide(k);
      if (this._drawnN.has(numKey(k, Math.floor((x - 0.25) / s), Math.floor((z - 0.25) / s)))) return k;
    }
    return -1;
  }

  // the L0 skin (what the player stands on): the L0 node's own surface (no
  // walls — NOSTAND is never indexed), null where no L0 node is resident
  skinY(x, z) {
    const s = nodeSide(0), tx = Math.floor((x - 0.25) / s), ty = Math.floor((z - 0.25) / s);
    const n = this.nodes.get(`0_${tx}_${ty}`);
    if (!n || n.st !== 'ready') return null;
    const y = groundAt(this._meshOf(n), x - tx * s, z - ty * s);
    return Number.isFinite(y) ? y : null;
  }
}

